import React, { useState, useContext, useEffect, useRef } from 'react';
import './otp.scss';
import { Button, Popup, LoadingScreen } from 'components';
import { LendingContext } from 'context';
import { useNavService } from 'hooks';
import { routes } from 'router';
import { generateOtpApiCall, verifyOtpApiCall, TealiumService } from 'services';
import { OmfLogo } from 'assets';

const CONTACT_NUMBER = '086 010 2209';
const MAX_SUBMISSIONS = 3;
const OTP_TIMEOUT_MINUTES = 5;

export const Otp = () => {
  const { application } = useContext(LendingContext);
  const navigate = useNavService();

  const [digits, setDigits] = useState(Array(6).fill(''));
  const [submissions, setSubmissions] = useState(0);
  const [generatingOtp, setGeneratingOtp] = useState(true);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [otpAlive, setOtpAlive] = useState(true);
  const [showPopup, setShowPopup] = useState(false);

  const inputRefs = useRef([]);
  const otpDeadTimeout = useRef(null);
  const otpRequested = useRef(false);

  const mobileNumber =
    application?.personal?.mobileNumber || sessionStorage.getItem('mobileNumber') || '';
  const maskPhone = (phone) => `****${(phone || '').replace(/\D/g, '').slice(-4)}`;
  const isComplete = digits.every((d) => d !== '');
  const canContinue = isComplete && submissions < MAX_SUBMISSIONS && otpAlive && !verifyingOtp;

  useEffect(() => {
    if (otpRequested.current) { return; }
    otpRequested.current = true;
    generateOtpApiCall().finally(() => setGeneratingOtp(false));
    otpDeadTimeout.current = setTimeout(() => {
      setOtpAlive(false);
      const expiredOmfOffer = JSON.parse(sessionStorage.getItem('omfOffer') || 'null');
      TealiumService.onOtpExpired(expiredOmfOffer, application?.offers?.hashId);
    }, OTP_TIMEOUT_MINUTES * 60 * 1000);

    const omfOffer = JSON.parse(sessionStorage.getItem('omfOffer') || 'null');
    TealiumService.onOtpStart(omfOffer, application?.offers?.hashId);

    return () => clearTimeout(otpDeadTimeout.current);
  }, []);

  useEffect(() => {
    if (submissions >= MAX_SUBMISSIONS) {
      setShowPopup(true);
    }
  }, [submissions]);

  function handleChange(index, value) {
    const cleaned = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = cleaned;
    setDigits(next);
    if (cleaned && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  }

  function handleKeyDown(index, e) {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  }

  function handlePaste(e) {
    e.preventDefault();
    const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (!text.length) { return; }
    const next = Array(6).fill('');
    text.split('').forEach((ch, i) => {
      next[i] = ch;
    });
    setDigits(next);
    inputRefs.current[Math.min(text.length, 5)]?.focus();
  }

  function navigateForward() {
    navigate.to(routes.documents);
  }

  async function handleContinue() {
    if (!canContinue) { return; }

    const omfOffer = JSON.parse(sessionStorage.getItem('omfOffer') || 'null');
    const hashId = application?.offers?.hashId;
    TealiumService.onOtpContinue(omfOffer, hashId);

    setVerifyingOtp(true);
    try {
      const response = await verifyOtpApiCall(digits.join(''));
      if (response?.data?.verified === true) {
        navigateForward();
        return;
      }
      const newCount = submissions + 1;
      TealiumService.onOtpIncorrect(omfOffer, hashId, newCount, MAX_SUBMISSIONS);
      setSubmissions(newCount);
      setDigits(Array(6).fill(''));
      inputRefs.current[0]?.focus();
    } finally {
      setVerifyingOtp(false);
    }
  }

  function handleResend() {
    const omfOffer = JSON.parse(sessionStorage.getItem('omfOffer') || 'null');
    TealiumService.onOtpResend(omfOffer, application?.offers?.hashId);
    setDigits(Array(6).fill(''));
    setGeneratingOtp(true);
    generateOtpApiCall()
      .then(() => {
        clearTimeout(otpDeadTimeout.current);
        setOtpAlive(true);
        otpDeadTimeout.current = setTimeout(() => {
          setOtpAlive(false);
          const expiredOmfOffer = JSON.parse(sessionStorage.getItem('omfOffer') || 'null');
          TealiumService.onOtpExpired(expiredOmfOffer, application?.offers?.hashId);
        }, OTP_TIMEOUT_MINUTES * 60 * 1000);
      })
      .finally(() => {
        setGeneratingOtp(false);
      });
    inputRefs.current[0]?.focus();
  }

  if (generatingOtp) {
    return <LoadingScreen loadingMessage='Sending your OTP' secondaryMessage='This should only take a moment' />;
  }

  return (
    <section className='otp-page'>
      <header className='otp-header'>
        <button className='otp-header__back' onClick={() => navigate.back()} aria-label='Go back'>
          &#8249;
        </button>
        <h1 className='otp-header__title'>OTP</h1>
      </header>

      <main className='otp-content'>
        <div className='otp-panel'>
          <h2 className='otp-panel__heading'>Please enter your one-time pin (OTP)</h2>
          <p className='otp-panel__description'>
            Please enter the one-time PIN (OTP) that was sent to {maskPhone(mobileNumber)}. This
            code is valid for {OTP_TIMEOUT_MINUTES} minutes.
          </p>

          <div className='otp-inputs' onPaste={handlePaste}>
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(el) => (inputRefs.current[index] = el)}
                className={`otp-input${!otpAlive ? ' otp-input--error' : ''}`}
                type='tel'
                inputMode='numeric'
                maxLength={1}
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                aria-label={`OTP digit ${index + 1}`}
              />
            ))}
          </div>

          <div className='otp-status'>
            <button
              type='button'
              className='otp-resend'
              onClick={handleResend}
              disabled={submissions >= MAX_SUBMISSIONS || generatingOtp}
            >
              Resend OTP
            </button>

            {otpAlive && submissions > 0 && submissions < MAX_SUBMISSIONS && (
              <p className='otp-attempts'>
                OTP incorrect ({submissions}/{MAX_SUBMISSIONS})
              </p>
            )}
            {(!otpAlive || submissions >= MAX_SUBMISSIONS) && (
              <p className='otp-expired'>OTP expired</p>
            )}
          </div>

          <p className='otp-help'>Call {CONTACT_NUMBER} if you need help.</p>
        </div>

        <div className='otp-footer-area'>
          <div className='otp-footer-area__buttons'>
            <Button secondary onClick={() => navigate.back()}>
              Back
            </Button>
            <Button primary onClick={handleContinue} disabled={!canContinue}>
              Continue
            </Button>
          </div>

          <div className='otp-branding'>
            <img src={OmfLogo} alt='In association with Old Mutual Finance' className='otp-branding__omf-logo' />
          </div>
        </div>
      </main>

      {showPopup && (
        <Popup
          headText="You've reached the maximum number of OTP attempts."
          secondaryMessage={`Please contact our call centre on ${CONTACT_NUMBER}`}
          buttonMessage='Close'
          togglePopup={() => setShowPopup(false)}
        />
      )}
    </section>
  );
};
